import React, { useState, useEffect, useMemo } from 'react';
import { Select, Tooltip, Dropdown, Button, Alert, Spin, Modal } from 'antd';
import { QuestionCircleOutlined, MoreOutlined } from '@ant-design/icons';
import s from './s.module.less';
import PauseProListingsModal from '../PauseProListingsModal';
import ProListingReportModal from '../ProListingReportModal';
import PerformanceChart from '../PerformanceChart';
import { useNavigate } from 'react-router-dom';
import { PATH } from 'constants/path';
import useBudgetInfo from 'hooks/marketing/useBudgetInfo';
import useAdMetrics from 'hooks/marketing/useAdMetrics';
import useProviderKlarityServiceList from 'hooks/useProviderKlarityServiceList';
import { isInHours, formatUTCTime } from 'utils/marketing';
import EmptyBlue from 'assets/common/emptyBlue.svg';
import WarningTips from 'components/WarningTips';
import isNil from 'lodash/isNil';
import dayjs from 'dayjs';
import useProviderHomeInfo from 'hooks/useProviderHomeInfo';
import { EFeatureType } from 'types/provider';
import ProListingUpsellCard from '../ProListingUpsellCard';

const { Option } = Select;

type Props = {
    hasJoinedAdSystem: boolean;
    joinedAt?: string;
    onSetupClick?: () => void;
    isReadOnly?: boolean;
};

const PerformanceCard = (props: Props) => {
    const { hasJoinedAdSystem, joinedAt, onSetupClick, isReadOnly } = props;
    const navigate = useNavigate();
    const [selectedPeriod, setSelectedPeriod] = useState('1');
    const [selectedState, setSelectedState] = useState('All states');
    const [selectedSpecialty, setSelectedSpecialty] = useState('All specialties');
    const [pauseModalVisible, setPauseModalVisible] = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showCreditModal, setShowCreditModal] = useState(false);
    const [showNoReportModal, setShowNoReportModal] = useState(false);

    const [budgetInfo, budgetStatus, budgetLoading, refetchBudgetInfo] = useBudgetInfo();
    const [metrics, metricsLoading, fetchMetrics] = useAdMetrics({
        week: 1,
    });
    const [providerKlarityServiceList] = useProviderKlarityServiceList();
    const [data, loadingHome, fetchData] = useProviderHomeInfo();

    const createdLocal = dayjs.utc(joinedAt).local();
    const isOlderThan2Weeks = dayjs().diff(createdLocal, 'week') >= 2;
    const showDroppedBanner =
        !hasJoinedAdSystem &&
        ((!isNil(metrics.totalBookingsChangeRate) && metrics.totalBookingsChangeRate < -10) ||
            (!isNil(metrics.impressionsChangeRate) && metrics.impressionsChangeRate < -10) ||
            (!isNil(metrics.impressionShareChangeRate) && metrics.impressionShareChangeRate < -10));
    const isPPAProvider = !!data?.ppaProvider;
    const showProListingReport = !!data?.features?.includes(EFeatureType.PROLISTING_REPORT);
    const showOpportunityBanner = !hasJoinedAdSystem && !showDroppedBanner;
    //const showOpportunityBanner  = true;

    const selectOptions = useMemo(() => {
        const specialties: string[] = [];
        const states: string[] = [];

        if (!providerKlarityServiceList) {
            return {
                specialties: [],
                states: [],
            };
        }

        providerKlarityServiceList.forEach((item) => {
            if (item.serviceSpecialtyName && !specialties.includes(item.serviceSpecialtyName)) {
                specialties.push(item.serviceSpecialtyName);
            }
            if (item.state && !states.includes(item.state)) {
                states.push(item.state);
            }
        });

        return {
            specialties: ['All specialties', ...specialties.sort()],
            states: ['All states', ...states.sort()],
        };
    }, [providerKlarityServiceList]);

    useEffect(() => {
        fetchMetrics({
            week: parseInt(selectedPeriod, 10),
            state: selectedState === 'All states' ? undefined : selectedState,
            specialty: selectedSpecialty === 'All specialties' ? undefined : selectedSpecialty,
        });
    }, [selectedPeriod, selectedState, selectedSpecialty]);

    const formatChange = (value: number) => {
        if (value === 0) return '— 0%';
        const sign = value > 0 ? '↑' : '↓';
        return `${sign} ${Math.abs(value)}%`;
    };

    // 判断时间段选项是否应该被禁用
    const isOptionDisabled = (weeks: number): boolean => {
        if (!budgetInfo.createdAt) return false;

        // 从创建时间到现在经过了多少周
        const createdDate = new Date(budgetInfo.createdAt);
        const now = new Date();

        const timeDifference = now.getTime() - createdDate.getTime();
        const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;

        const weeksSinceCreated = Math.ceil(timeDifference / oneWeekInMs);
        return weeksSinceCreated < weeks;
    };

    const tooltips = {
        bookings: hasJoinedAdSystem
            ? 'Total number of new patient appointments booked through both Standard Listing and Pro-Listings.'
            : 'Total number of new patient appointments booked through Standard Listing.',
        impressions: 'How many times your profile was shown to patients in Klarity search results.',
        impressionShare:
            'The percentage of total impressions your profile received compared to all impressions received by providers similar to you in terms of specialty and state.',
        totalSpend: "The total amount you've spent on Pro-Listings bookings so far.",
    };

    const moreActionsItems = [
        {
            key: 'pause',
            label: 'Pause campaign',
            disabled: isReadOnly,
            onClick: () => setPauseModalVisible(true),
        },
        {
            key: 'credit',
            label: 'Free credit',
            onClick: () => setShowCreditModal(true),
        },
    ];

    const handlePeriodChange = (value: string) => {
        setSelectedPeriod(value);
    };

    const bannerRender = () => {
        let message = '';
        let action = null;
        const resumeNowAction = () => {
            return (
                <div className={s.action} onClick={onSetupClick}>
                    {budgetStatus.isBudgetNearlyExhausted || budgetStatus.isCostAllBudget
                        ? 'Increase budget'
                        : 'Resume now'}
                </div>
            );
        };

        if (budgetStatus.isPaused) {
            message = `Your Pro-Listings was paused on ${formatUTCTime(budgetInfo.updatedAt)}. You can resume it anytime to continue reaching more patients.`;
            action = resumeNowAction();
        }  else if (budgetStatus.isCostAllBudget) {
            message = `Your Pro-Listings is paused — budget used up on ${formatUTCTime(budgetInfo.updatedAt)}. Increase your budget to resume visibility.`;
            action = resumeNowAction();
        } else if (budgetStatus.isBudgetNearlyExhausted) {
            message =
                'You’ve used 80% of your Pro-Listings budget — increase it soon to avoid visibility interruption.';
            action = resumeNowAction();
        } else if (budgetInfo.createdAt && isInHours(budgetInfo.createdAt, 24)) {
            message =
                'No data yet? That’s normal — your Pro-Listing is live and results may take up to 24 hours to show.';
        } 

        return (
            message && (
                <Alert
                    className={s.proListingBanner}
                    message={message}
                    type="info"
                    showIcon
                    action={action}
                    icon={
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M8.25706 3.09858C9.02167 1.73928 10.9788 1.73928 11.7434 3.09858L17.3237 13.0191C18.0736 14.3523 17.1102 15.9996 15.5805 15.9996H4.4199C2.89025 15.9996 1.92682 14.3523 2.67675 13.0191L8.25706 3.09858ZM11.0001 12.9998C11.0001 13.552 10.5524 13.9998 10.0001 13.9998C9.44784 13.9998 9.00012 13.552 9.00012 12.9998C9.00012 12.4475 9.44784 11.9998 10.0001 11.9998C10.5524 11.9998 11.0001 12.4475 11.0001 12.9998ZM10.0001 4.99976C9.44784 4.99976 9.00012 5.44747 9.00012 5.99976V8.99976C9.00012 9.55204 9.44784 9.99976 10.0001 9.99976C10.5524 9.99976 11.0001 9.55204 11.0001 8.99976V5.99976C11.0001 5.44747 10.5524 4.99976 10.0001 4.99976Z"
                                fill="#FBBF24"
                            />
                        </svg>
                    }
                />
            )
        );
    };

    const handleAddAvailability = () => {
        if (isPPAProvider) {
            navigate(`${PATH.DASHBOARD}/${PATH.CALENDAR_SETTING}`);
        } else {
            window.open('https://my.helloklarity.com', '_blank');
        }
    };

    const droppedBannerRender = () => {
        return (
            <div className="mb-4">
                <WarningTips
                    title="Your Pro-Listings performance has dropped significantly. Please check your listing and adjust your budget."
                    description="Your profile visibility has recently declined. Join Pro-Listings to reach more patients and stay ahead of other providers in your area."
                    content={
                        <Button
                            className={s.addAvailabilityButton}
                            onClick={() => navigate(`${PATH.DASHBOARD}/${PATH.MARKETING}?tab=prolisting`)}
                        >
                            Add more availability
                        </Button>
                    }
                    closable
                />
            </div>
        );
    };
    return (
        <div className={s.container}>
            <div className={s.header}>
                <h2 className={s.title}>{hasJoinedAdSystem ? 'Listing performance' : 'Performance'}</h2>

                <div className={s.controls}>
                    {hasJoinedAdSystem && showProListingReport && (
                        <Button
                            onClick={() => {
                                if (isOlderThan2Weeks) {
                                    setShowReportModal(true);
                                } else {
                                    setShowNoReportModal(true);
                                }
                            }}
                            style={{ backgroundColor: '#fff' }}
                        >
                            View report
                        </Button>
                    )}
                    <Select
                        value={selectedPeriod}
                        onChange={(value) => handlePeriodChange(value)}
                        className={s.select}
                        dropdownStyle={{ minWidth: '160px' }}
                    >
                        <Option value="1">Last 1 week</Option>
                        <Option value="2" disabled={isOptionDisabled(2)}>
                            Last 2 weeks
                        </Option>
                        <Option value="4" disabled={isOptionDisabled(4)}>
                            Last 4 weeks
                        </Option>
                        <Option value="8" disabled={isOptionDisabled(8)}>
                            Last 8 weeks
                        </Option>
                        <Option value="12" disabled={isOptionDisabled(12)}>
                            Last 12 weeks
                        </Option>
                        <Option value="0">Since you started</Option>
                    </Select>
                    {budgetInfo.enableAdvancedSettings && (
                        <>
                            <Select
                                value={selectedSpecialty}
                                onChange={(value) => setSelectedSpecialty(value)}
                                className={s.select}
                                style={{ minWidth: '160px' }}
                                dropdownStyle={{ minWidth: '220px' }}
                            >
                                {selectOptions.specialties.map((specialty) => (
                                    <Option key={specialty} value={specialty}>
                                        {specialty}
                                    </Option>
                                ))}
                            </Select>
                            <Select
                                value={selectedState}
                                onChange={(value) => setSelectedState(value)}
                                className={s.select}
                            >
                                {selectOptions.states.map((state) => (
                                    <Option key={state} value={state}>
                                        {state}
                                    </Option>
                                ))}
                            </Select>
                        </>
                    )}
                </div>
            </div>
            {hasJoinedAdSystem && (
                <div className="text-[#6B7280] mb-4 mt-[-8px]">
                    Your total performance across standard and Pro-Listings.
                </div>
            )}
            {showDroppedBanner && droppedBannerRender()}
            {bannerRender()}
            <Spin spinning={metricsLoading || budgetLoading}>
                <div
                    className={s.metricsGrid}
                    style={{ gridTemplateColumns: hasJoinedAdSystem ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)' }}
                >
                    <div className={s.metricCard}>
                        <div className={s.metricHeader}>
                            <span className={s.metricLabel}>Bookings</span>
                            <Tooltip title={tooltips.bookings} color="#fff" overlayInnerStyle={{ color: '#333' }}>
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                        <div className={s.metricContent}>
                            <div className={s.metricValue}>
                                <span className={s.value}>{metrics.totalBookings || 0}</span>
                                {!isNil(metrics.previousTotalBookings) && (
                                    <span className={s.baseValue}>from {metrics.previousTotalBookings}</span>
                                )}
                            </div>
                            {!isNil(metrics.totalBookingsChangeRate) && (
                                <div
                                    className={`${s.changeValue} ${(metrics.totalBookingsChangeRate < 0 && s.danger) || (metrics.totalBookingsChangeRate > 0 && s.positive)}`}
                                >{`${formatChange(metrics.totalBookingsChangeRate)}`}</div>
                            )}
                        </div>
                    </div>

                    <div className={s.metricCard}>
                        <div className={s.metricHeader}>
                            <span className={s.metricLabel}>Impressions</span>
                            <Tooltip title={tooltips.impressions} color="#fff" overlayInnerStyle={{ color: '#333' }}>
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                        <div className={s.metricContent}>
                            <div className={s.metricValue}>
                                <span className={s.value}>{metrics.totalImpressions || 0}</span>
                                {!isNil(metrics.previousTotalImpressions) && (
                                    <span className={s.baseValue}>from {metrics.previousTotalImpressions}</span>
                                )}
                            </div>
                            {!isNil(metrics.impressionsChangeRate) && (
                                <div
                                    className={`${s.changeValue} ${(metrics.impressionsChangeRate < 0 && s.danger) || (metrics.impressionsChangeRate > 0 && s.positive)}`}
                                >{`${formatChange(metrics.impressionsChangeRate)}`}</div>
                            )}
                        </div>
                    </div>

                    <div className={s.metricCard}>
                        <div className={s.metricHeader}>
                            <span className={s.metricLabel}>Impression share</span>
                            <Tooltip
                                title={tooltips.impressionShare}
                                color="#fff"
                                overlayInnerStyle={{ color: '#333' }}
                            >
                                <QuestionCircleOutlined className={s.infoIcon} />
                            </Tooltip>
                        </div>
                        <div className={s.metricContent}>
                            <div className={s.metricValue}>
                                <span className={s.value}>{metrics.impressionShareRate}%</span>
                                {!isNil(metrics.previousImpressionShareRate) && (
                                    <span className={s.baseValue}>from {metrics.previousImpressionShareRate}%</span>
                                )}
                            </div>
                            {!isNil(metrics.impressionShareChangeRate) && (
                                <div
                                    className={`${s.changeValue} ${(metrics.impressionShareChangeRate < 0 && s.danger) || (metrics.impressionShareChangeRate > 0 && s.positive)}`}
                                >{`${formatChange(metrics.impressionShareChangeRate)}`}</div>
                            )}
                        </div>
                    </div>

                    {hasJoinedAdSystem && (
                        <div className={s.metricCard}>
                            <div className={s.metricHeader}>
                                <span className={s.metricLabel}>Total spend</span>
                                <Tooltip title={tooltips.totalSpend} color="#fff" overlayInnerStyle={{ color: '#333' }}>
                                    <QuestionCircleOutlined className={s.infoIcon} />
                                </Tooltip>
                            </div>
                            <div className={s.metricContent}>
                                <div className={s.metricValue}>
                                    <span className={s.value}>${metrics.totalSpend}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {showOpportunityBanner && <ProListingUpsellCard onSetupClick={onSetupClick} />}

                <div className={s.chartSection}>
                    <div className={s.chartHeader}>
                        <h3 className={s.chartTitle}>Performance trend</h3>
                        {hasJoinedAdSystem &&
                            (budgetStatus.isCostAllBudget || budgetStatus.isPaymentFailed || budgetStatus.isPaused ? (
                                <Button type="primary" onClick={onSetupClick} className={s.resumeButton}>
                                    Resume listing
                                </Button>
                            ) : (
                                <Dropdown
                                    menu={{
                                        items: moreActionsItems,
                                    }}
                                    trigger={['click']}
                                    placement="bottomRight"
                                >
                                    <Button className={s.moreButton}>
                                        More actions
                                        <MoreOutlined className={s.moreIcon} />
                                    </Button>
                                </Dropdown>
                            ))}
                    </div>
                    <PerformanceChart
                        data={metrics.trendsDisplay}
                        specialty={selectedSpecialty === 'All specialties' ? undefined : selectedSpecialty}
                        state={selectedState === 'All states' ? undefined : selectedState}
                    />
                </div>
            </Spin>

            <PauseProListingsModal
                visible={pauseModalVisible}
                onCancel={() => setPauseModalVisible(false)}
                onConfirm={() => {
                    fetchMetrics({
                        week: 1,
                    });
                    refetchBudgetInfo();
                }}
            />
            <Modal open={showCreditModal} onCancel={() => setShowCreditModal(false)} footer={null} title="Free credits">
                <div className="text-base text-gray-500 mb-6">View your available free credits for Pro-Listings.</div>
                <div className={s.creditCard}>
                    <div className="flex items-center gap-6 mb-1">
                        Free credits remaining
                        <Tooltip
                            title="The amount of free credits you have left before paid spend begins."
                            color="#fff"
                            overlayInnerStyle={{ color: '#333' }}
                        >
                            <QuestionCircleOutlined className={s.infoIcon} />
                        </Tooltip>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-2xl font-semibold">${budgetInfo.creditBalance?.remaining}</span>
                        <span className="text-gray-500">/ ${budgetInfo.creditBalance?.total || 150}</span>
                    </div>
                </div>
            </Modal>
            <Modal open={showNoReportModal} onCancel={() => setShowNoReportModal(false)} footer={null} width={676}>
                <div className="flex flex-col items-center justify-center mb-6">
                    <img src={EmptyBlue} alt="empty" />
                    <div className="text-gray-900 text-xl font-medium mb-2">Your report is on the way</div>
                    <div className="text-gray-500 text-base">
                        Reports generate automatically every two weeks after you join Pro-Listings.
                    </div>
                    <div className="text-gray-500 text-base">
                        Check back soon to see your performance insights and booking trends.
                    </div>
                </div>
            </Modal>
            <ProListingReportModal
                open={showReportModal}
                onCancel={() => setShowReportModal(false)}
                onAddAvailability={handleAddAvailability}
            />
        </div>
    );
};

export default PerformanceCard;
